import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '@/lib/theme.js';
import { useScrollProgress } from '../hooks/useScrollProgress.js';
import { Logo } from '@/components/Logo.js';
import { MoonIcon, SunIcon } from '../components/icons.js';
import { preferredStoreUrl } from '@/lib/appStores.js';

// Hash links use the `/#…` form so they work from any route: on the landing
// they scroll in place; on a footer page they navigate to the landing and
// then scroll to the section.
const navLinks = [
  { href: '/#this-day', label: 'This Day' },
  { href: '/#manifesto', label: 'The Way' },
  { href: '/#rhythm', label: 'Rhythm' },
  { href: '/#modules', label: 'Modules' },
  { href: '/#engine', label: 'Engine' },
  { href: '/#faq', label: 'FAQ' },
];

export function Nav() {
  const { theme, toggle } = useTheme();
  const progress = useScrollProgress(80);
  const [open, setOpen] = useState(false);

  // Lock scroll when mobile menu open
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  return (
    <>
      <header
        className="fixed top-0 left-0 right-0 z-40 ow-fade-down"
        style={{
          height: 'var(--ow-nav-h)',
          // @ts-expect-error custom prop
          '--ow-nav-h': '72px',
          backdropFilter: progress > 0.05 ? 'blur(20px)' : 'none',
          WebkitBackdropFilter: progress > 0.05 ? 'blur(20px)' : 'none',
          background: `color-mix(in srgb, var(--ow-bg) ${85 * progress}%, transparent)`,
          borderBottom: progress > 0.5 ? '1px solid var(--ow-border)' : '1px solid transparent',
          transition: 'background 200ms ease, border-color 200ms ease',
        }}
      >
        <div className="mx-auto max-w-container px-5 md:px-8 h-[60px] md:h-[72px] flex items-center justify-between gap-4">
          <Link
            to="/"
            aria-label="Orthodox Way home"
            className="flex items-center gap-3 group ow-focusable"
          >
            <span className="text-gold inline-flex">
              <Logo size={26} />
            </span>
            <span
              className="font-display text-primary uppercase hidden sm:inline"
              style={{ fontSize: 13, fontWeight: 500, letterSpacing: '0.18em' }}
            >
              Orthodox Way
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="ow-underline font-display uppercase text-secondary"
                style={{ fontSize: 11, letterSpacing: '0.22em' }}
              >
                {link.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3 md:gap-5">
            <button
              type="button"
              onClick={toggle}
              aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
              className="ow-focusable w-8 h-8 rounded-full border border-border flex items-center justify-center text-secondary hover:text-gold hover:border-gold transition"
            >
              {theme === 'light' ? <MoonIcon /> : <SunIcon />}
            </button>
            <Link
              to="/login"
              className="ow-underline hidden sm:inline-block font-display uppercase text-secondary md:ml-2"
              style={{ fontSize: 11, letterSpacing: '0.18em' }}
            >
              Sign in
            </Link>
            <a
              href={preferredStoreUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="ow-cta-primary hidden md:inline-flex md:ml-2"
            >
              Get the App
            </a>
            <button
              type="button"
              className="lg:hidden ow-focusable w-8 h-8 rounded-full border border-border flex items-center justify-center text-secondary"
              aria-label={open ? 'Close menu' : 'Open menu'}
              aria-expanded={open}
              aria-controls="mobile-menu"
              onClick={() => setOpen((o) => !o)}
            >
              <span aria-hidden style={{ fontSize: 16 }}>
                {open ? '×' : '≡'}
              </span>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile fullscreen menu */}
      {open && (
        <div
          id="mobile-menu"
          className="fixed inset-0 z-30 bg-bg flex flex-col items-center justify-center gap-7 lg:hidden"
          role="dialog"
          aria-modal="true"
        >
          {navLinks.map((link, i) => (
            <a
              key={link.href}
              href={link.href}
              onClick={() => setOpen(false)}
              className="font-display text-primary hover:text-gold transition ow-fade-up"
              style={{
                fontSize: 32,
                letterSpacing: '0.04em',
                animationDelay: `${i * 60}ms`,
              }}
            >
              {link.label}
            </a>
          ))}
          <a
            href={preferredStoreUrl()}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            className="ow-cta-primary mt-4"
          >
            Get the App
          </a>
        </div>
      )}
    </>
  );
}
